import { LookupResponse } from '@core/models/common.model';
import { lookupMetadata } from '@core/lookup/lookup-metadata';
import { Lookups, lookupItems } from '@core/lookup/lookup-utils';

// Reading the reference lists the intake form is given. Pure, so the steps and the form share one implementation.


// The order the review form lists these in; a code that is not listed goes after them where a list allows extras.
export const ROW_ORDER = {
  patternTypes: ['EVENTS', 'BEHAVIOURS', 'THOUGHTS', 'BELIEFS'],
  fourPFactorTypes: ['PREDISPOSING', 'PRECIPITATING', 'PERPETUATING', 'PROTECTIVE'],
  fourPDimensionTypes: ['BIOLOGICAL', 'PSYCHOLOGICAL', 'SOCIAL_ECONOMIC'],
  clientLimitationTypes: ['BIOLOGICAL', 'PSYCHOLOGICAL', 'SOCIAL_ECONOMIC'],
  riskScreeningTypes: [
    'SUICIDAL_THOUGHTS_OF_HURTING_SELF_OR_OTHERS',
    'ALCOHOL_ABUSE_TO_COPE_WITH_STRESSORS',
    'ANXIETY',
    'DEPRESSIVE_DISORDER',
    'DEPRESSED_MOOD',
    'DRUG_ADDICTION',
    'MENTAL_HEALTH_AND_SUBSTANCE_ABUSE',
    'STRESS_AND_BURNOUT_SIGNS_OF_NOT_COPING',
  ],
  symptomClusterComplexityTypes: ['LONG_TERM', 'COMPLEX', 'MEDICAL_SPECIALIZED', 'SIMPLE_PRESENTATION'],
  workPerformanceImpactTypes: [
    'ABSENTEEISM',
    'DECREASED_CONCENTRATION_LEVELS',
    'DEMOTIVATED',
    'MAKING_ERRORS',
    'MULTIPLE_ISSUES',
    'PERFORMANCE_MANAGEMENT_ISSUES',
    'PRESENTEEISM',
    'RELATIONSHIP_ISSUES_AT_WORK',
    'SUBSTANCE_ABUSE',
    'SAFETY_RISK_ISSUES',
    'TIMEKEEPING',
  ],
} as const;

export const FOUR_P_HINTS: Record<string, string> = {
  PREDISPOSING: 'What is making it more likely for the client to experience these concerns: background or history, biological, socio-economic.',
  PRECIPITATING: 'What events have triggered this concern to start.',
  PERPETUATING: 'What internal or external factors enable the problem?',
  PROTECTIVE: 'What are the positive influences that can help the client deal with the concern.',
};

// The items of a list in the order of the review form (ROW_ORDER); with extra, unlisted items follow.
export function ordered(lookups: Lookups, key: keyof typeof ROW_ORDER, extra = false): LookupResponse[] {
  const order: readonly string[] = ROW_ORDER[key];
  const items = lookupItems(lookups, key);
  const listed = order.map((code) => items.find((item) => item.code === code)).filter((item): item is LookupResponse => !!item);
  return extra ? [...listed, ...items.filter((item) => !order.includes(item.code ?? '') && item.code !== 'NO_IMPACT')] : listed;
}

// Every item of a list, in the order of the review form, for the lists that are ticked. "Other" is always the last choice.
export function checklistItems(lookups: Lookups, key: 'riskScreeningTypes'): LookupResponse[] {
  const items = ordered(lookups, key, true);
  return [...items.filter((item) => item.code !== 'OTHER'), ...items.filter((item) => item.code === 'OTHER')];
}

// A screening is named for what it looks for, and the tool that does it.
export function screeningLabel(item: LookupResponse): string {
  const tool = lookupMetadata<{ screeningTool?: string }>(item)?.screeningTool;
  return tool ? `${item.name ?? item.code} (${tool})` : (item.name ?? item.code ?? '');
}

// ---- Problem coding: a problem belongs to a cluster through its Metadata ----------------------------------------------------

function clusterCode(lookups: Lookups, clusterId: string | null | undefined): string | null {
  return lookupItems(lookups, 'problemClusters').find((item) => item.id === clusterId)?.code ?? null;
}

function problemClusterCode(problem: LookupResponse): string | null {
  return lookupMetadata<{ cluster?: string }>(problem)?.cluster ?? null;
}

// The problems of one cluster, for the problem pick that follows the cluster pick.
export function problemsFor(lookups: Lookups, clusterId: string | null | undefined): LookupResponse[] {
  const code = clusterCode(lookups, clusterId);
  return code ? lookupItems(lookups, 'problems').filter((item) => problemClusterCode(item) === code) : [];
}

// The underlying problems are offered under their cluster; "Not applicable" belongs to none and comes first.
export function underlyingProblemGroups(lookups: Lookups): { name: string; problems: LookupResponse[] }[] {
  return [...lookupItems(lookups, 'problemClusters')]
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    .map((cluster) => ({ name: cluster.name ?? cluster.code ?? '', problems: problemsFor(lookups, cluster.id) }))
    .filter((group) => group.problems.length > 0);
}

export function notApplicableProblems(lookups: Lookups): LookupResponse[] {
  return lookupItems(lookups, 'problems').filter((item) => !problemClusterCode(item));
}

// The id of the problem that stands alone ("Not applicable"), from its Metadata.
export function exclusiveProblemId(lookups: Lookups): string | undefined {
  return lookupItems(lookups, 'problems').find((item) => lookupMetadata<{ exclusive?: boolean }>(item)?.exclusive)?.id;
}
